import { z } from 'zod';

/**
 * Zod Schema für Season — Runtime-Validierung + Type-Inference.
 * Gruppiert Events nach Spielzeit und steuert season-spezifische Konfiguration.
 * 4 Felder: id, name, sortOrder, isActive, scannerIdField, ticketLayoutJson, createdAt, updatedAt
 */
export const SeasonSchema = z.object({
  id: z.string().cuid(),
  /** Anzeigename, z.B. "Season 4" oder "Saison 2026" */
  name: z.string().min(1),
  /** Sortierreihenfolge (niedrigere Zahl = frühere Season) */
  sortOrder: z.number().int().default(0),
  /** Ob die Season aktiv ist */
  isActive: z.boolean().default(true),
  /**
   * Ticket-Feld das vom Scanner als Barcode-ID verwendet wird.
   * Standard: "pdfDriveId" — kann auf andere Ticket-Felder (z.B. "externalId") gesetzt werden.
   */
  scannerIdField: z.string().min(1).default('pdfDriveId'),
  /**
   * Optionale JSON-Konfiguration für das Ticket-Layout.
   * Reserviert für zukünftige Ticket-Design-Anpassungen pro Season.
   */
  ticketLayoutJson: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type Season = z.infer<typeof SeasonSchema>;

/**
 * Schema für Season-Erstellung.
 */
export const CreateSeasonSchema = SeasonSchema.pick({
  name: true,
  sortOrder: true,
  isActive: true,
  scannerIdField: true,
  ticketLayoutJson: true,
});

/** TypeScript Type für Season-Erstellung */
export type CreateSeason = z.infer<typeof CreateSeasonSchema>;

/**
 * Schema für Season-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateSeasonSchema = CreateSeasonSchema.partial();

/** TypeScript Type für Season-Update */
export type UpdateSeason = z.infer<typeof UpdateSeasonSchema>;

/**
 * Paginierte Antwort von GET /seasons.
 */
export const SeasonListResponseSchema = z.object({
  /** Liste der Seasons */
  data: z.array(SeasonSchema),
  /** Gesamtanzahl */
  total: z.number().int().nonnegative(),
});

/** TypeScript Type für die paginierte Season-Antwort */
export type SeasonListResponse = z.infer<typeof SeasonListResponseSchema>;

/** Alias für Season — Kompatibilität mit Dashboard-Entities */
export type SeasonResponse = Season;

/**
 * Filter-Parameter für die Season-Liste.
 */
export interface SeasonListFilters {
  /** Nur aktive/inaktive Seasons */
  isActive?: boolean;
}
